import { createContext, useContext, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTenantSettingsQuery } from "@/hooks/useTenantSettingsQuery";
import { TenantCorporateSettings } from "@/types/tenant";
import {
  hexToHsl,
  hslToCssValue,
  getContrastForeground,
  adjustForDarkMode,
  getAccentBackground,
  getAccentForeground,
  getAccentBackgroundDark,
  getAccentForegroundDark,
} from "@/lib/colorUtils";

const TenantThemeContext = createContext<null>(null);

const CSS_VARS_LIGHT = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
  "--accent",
  "--accent-foreground",
] as const;

const CSS_VARS_DARK = [
  "--primary",
  "--primary-foreground",
  "--ring",
  "--sidebar-primary",
  "--sidebar-primary-foreground",
  "--sidebar-ring",
  "--accent",
  "--accent-foreground",
] as const;

/** Apply tenant brand colors as CSS custom properties on :root and .dark */
function applyTenantColors(settings: TenantCorporateSettings | undefined) {
  if (!settings) return;

  const root = document.documentElement;

  // --- Primary color ---
  if (settings.primary_color) {
    const hsl = hexToHsl(settings.primary_color);
    if (hsl) {
      const lightValue = hslToCssValue(hsl);
      const darkHsl = adjustForDarkMode(hsl);
      const darkValue = hslToCssValue(darkHsl);
      const foreground = getContrastForeground(settings.primary_color);

      // Light mode values (set on :root)
      root.style.setProperty("--primary", lightValue);
      root.style.setProperty("--primary-foreground", foreground);
      root.style.setProperty("--ring", lightValue);

      // Sidebar uses a slightly brighter version
      const sidebarHsl = { ...hsl, l: Math.min(hsl.l + 10, 70) };
      root.style.setProperty("--sidebar-primary", hslToCssValue(sidebarHsl));
      root.style.setProperty("--sidebar-primary-foreground", "0 0% 100%");
      root.style.setProperty("--sidebar-ring", hslToCssValue(sidebarHsl));

      // Chart color 1 follows primary
      root.style.setProperty("--chart-1", lightValue);

      // Store dark-mode values as separate properties that .dark can pick up
      root.style.setProperty("--tenant-primary-dark", darkValue);
      root.style.setProperty("--tenant-primary-foreground-dark", foreground);
    }
  }

  // --- Accent color ---
  if (settings.accent_color) {
    const hsl = hexToHsl(settings.accent_color);
    if (hsl) {
      root.style.setProperty("--accent", getAccentBackground(hsl));
      root.style.setProperty("--accent-foreground", getAccentForeground(hsl));

      // Dark mode accent
      root.style.setProperty("--tenant-accent-dark", getAccentBackgroundDark(hsl));
      root.style.setProperty("--tenant-accent-foreground-dark", getAccentForegroundDark(hsl));
    }
  }
}

/** Remove all tenant-injected CSS custom properties */
function removeTenantColors() {
  const root = document.documentElement;
  const allVars = [
    ...CSS_VARS_LIGHT,
    "--chart-1",
    "--tenant-primary-dark",
    "--tenant-primary-foreground-dark",
    "--tenant-accent-dark",
    "--tenant-accent-foreground-dark",
  ];
  allVars.forEach((v) => root.style.removeProperty(v));
}

/**
 * Apply dark-mode overrides via a MutationObserver on the <html> class.
 * When .dark is present, override the CSS vars with the dark variants.
 */
function setupDarkModeObserver(settings: TenantCorporateSettings | undefined): (() => void) | null {
  if (!settings?.primary_color && !settings?.accent_color) return null;

  const root = document.documentElement;

  const applyDark = () => {
    const isDark = root.classList.contains("dark");
    if (isDark) {
      const darkPrimary = root.style.getPropertyValue("--tenant-primary-dark");
      if (darkPrimary) {
        root.style.setProperty("--primary", darkPrimary);
        root.style.setProperty("--ring", darkPrimary);
        const darkFg = root.style.getPropertyValue("--tenant-primary-foreground-dark");
        if (darkFg) root.style.setProperty("--primary-foreground", darkFg);
      }
      const darkAccent = root.style.getPropertyValue("--tenant-accent-dark");
      if (darkAccent) {
        root.style.setProperty("--accent", darkAccent);
        const darkAccentFg = root.style.getPropertyValue("--tenant-accent-foreground-dark");
        if (darkAccentFg) root.style.setProperty("--accent-foreground", darkAccentFg);
      }
    } else {
      // Re-apply light mode values
      applyTenantColors(settings);
    }
  };

  // Initial apply
  applyDark();

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === "class") {
        applyDark();
        break;
      }
    }
  });

  observer.observe(root, { attributes: true, attributeFilter: ["class"] });

  return () => observer.disconnect();
}

interface TenantThemeProviderProps {
  children: ReactNode;
}

export function TenantThemeProvider({ children }: TenantThemeProviderProps) {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? undefined;
  const { data: tenant } = useTenantSettingsQuery(tenantId);

  useEffect(() => {
    if (!tenant?.settings) {
      removeTenantColors();
      return;
    }

    applyTenantColors(tenant.settings);
    const cleanup = setupDarkModeObserver(tenant.settings);

    return () => {
      removeTenantColors();
      cleanup?.();
    };
  }, [tenant?.settings]);

  return <TenantThemeContext.Provider value={null}>{children}</TenantThemeContext.Provider>;
}

/**
 * Apply colors for live preview (called from TenantSettings page).
 * This is exported so the settings page can call it on color change.
 */
export function applyTenantColorsPreview(settings: Partial<TenantCorporateSettings>) {
  applyTenantColors(settings as TenantCorporateSettings);
}
